
import { Json } from "@/integrations/supabase/types";

export interface InstalledApp {
  name: string;
  version: string | null;
  installed_date?: string;
  bundle_id?: string;
  icon?: string;
}

interface LastKnownData {
  battery_level: number | null;
  battery_status: string | null;
  ip_address: string | null;
  os_version: string | null;
  installed_apps: InstalledApp[];
}

export interface DeviceAdditionalProperties {
  // Common properties
  UDID?: string;
  UserID?: string;
  WiFiMAC?: string;
  HasBattery?: boolean;
  EthernetMAC?: string;
  ProductName?: string;
  BatteryLevel?: number;
  BluetoothMAC?: string;
  BuildVersion?: string;
  IsSupervised?: boolean;
  NotOnConsole?: boolean;
  UserLongName?: string;
  UserShortName?: string;
  DeviceCapacity?: number;
  AwaitingConfiguration?: boolean;
  AvailableDeviceCapacity?: number;
  SupplementalBuildVersion?: string;
  iTunesStoreAccountIsActive?: boolean;
  
  // iOS specific
  TimeZone?: string;
  IsMultiUser?: boolean;
  ModelNumber?: string;
  IsNetworkTethered?: boolean;
  CellularTechnology?: number;
  DataRoamingEnabled?: boolean;
  EASDeviceIdentifier?: string;
  IsCloudBackupEnabled?: boolean;
  IsMDMLostModeEnabled?: boolean;
  ModemFirmwareVersion?: string;
  ServiceSubscriptions?: Array<{
    IMEI?: string;
    MEID?: string;
    EID?: string;
    Slot?: string;
  }>;
  IsDoNotDisturbInEffect?: boolean;
  PersonalHotspotEnabled?: boolean;
  iTunesStoreAccountHash?: string;
  IsActivationLockEnabled?: boolean;
  IsDeviceLocatorServiceEnabled?: boolean;
}

export interface Device {
  id: string;
  name: string;
  os_type: string;  // "macOS", "iOS", "iPadOS", "watchOS", "tvOS", "visionOS"
  os_version: string;
  status: string;
  last_seen: string;
  created_at: string;
  hostname: string;
  battery_level: number | null;
  battery_status: string | null;
  serial_number: string | null;
  ip_address: string | null;
  installed_apps: InstalledApp[];
  deviceAdditionalProperties?: DeviceAdditionalProperties;
  // Adding these properties to match what we're using in the code
  department?: string | null;
  location?: string | null;
  customer?: string | null;
  tenantId?: string | null;
  script_deployments?: Array<{
    id: string;
    status: string;
    last_run: string | null;
    exit_code: number | null;
    output: string | null;
    retry_enabled: boolean | null;
    configurations: {
      name: string;
    } | null;
  }>;
}

export const isValidInstalledApp = (app: unknown): app is InstalledApp => {
  if (typeof app !== 'object' || app === null) return false;
  const appObj = app as Record<string, unknown>;
  return typeof appObj.name === 'string';
};

export const convertToInstalledApp = (jsonApp: Json): InstalledApp | null => {
  if (typeof jsonApp !== 'object' || jsonApp === null) return null;
  
  const app = jsonApp as Record<string, unknown>;
  if (typeof app.name !== 'string') return null;

  return {
    name: app.name,
    version: typeof app.version === 'string' ? app.version : null,
    installed_date: typeof app.installed_date === 'string' ? app.installed_date : undefined,
    bundle_id: typeof app.bundle_id === 'string' ? app.bundle_id : undefined,
    icon: typeof app.icon === 'string' ? app.icon : undefined
  };
};

export const isValidLastKnownData = (data: unknown): data is LastKnownData => {
  if (typeof data !== 'object' || data === null) return false;
  const lastKnownData = data as Partial<LastKnownData>;
  return (
    (typeof lastKnownData.battery_level === 'number' || lastKnownData.battery_level === null) &&
    (typeof lastKnownData.battery_status === 'string' || lastKnownData.battery_status === null) &&
    (typeof lastKnownData.ip_address === 'string' || lastKnownData.ip_address === null) &&
    (typeof lastKnownData.os_version === 'string' || lastKnownData.os_version === null) &&
    Array.isArray(lastKnownData.installed_apps)
  );
};
